import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Loader2, Search, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { getArticles } from "@/lib/api";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    getArticles().then(setArticles).catch(() => setArticles([])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const counts = new Map();
    articles.forEach((a) => counts.set(a.category, (counts.get(a.category) || 0) + 1));
    return [{ name: "All", count: articles.length }, ...Array.from(counts.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count)];
  }, [articles]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      if (activeCategory !== "All" && a.category !== activeCategory) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        (a.excerpt || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    });
  }, [articles, activeCategory, query]);

  const [featured, ...rest] = filtered;

  return (
    <Layout>
      <Seo title="Blog & Articles" description="Guides and travel tips on Gatwick airport transfers, chauffeur travel and getting around Crawley and Surrey with Clocktower Cars Gatwick." path="/blog" />

      {/* HERO */}
      <section className="bg-white pt-14 pb-10 border-b border-[#EAEBEA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-4 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Blog & Articles · 3 fresh guides every week
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-ink">Insights & travel tips</h1>
          <p className="mt-5 text-lg text-ink/60 max-w-2xl">Locally-written guides on Gatwick airport transfers, chauffeur travel and getting around Crawley, Surrey and Sussex with ease.</p>
        </div>
      </section>

      {loading ? (
        <div className="py-32 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : articles.length === 0 ? (
        <div className="py-32 text-center text-ink/50">No articles yet.</div>
      ) : (
        <>
          {/* FILTER BAR */}
          <section data-testid="blog-filter-bar" className="bg-surface py-5 border-b border-[#EAEBEA] sticky top-[92px] md:top-[104px] z-20">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-thin -mx-1 px-1">
                {categories.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setActiveCategory(c.name)}
                    data-testid={`blog-cat-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                      activeCategory === c.name
                        ? "bg-ink text-white"
                        : "bg-white border border-[#EAEBEA] text-ink/70 hover:border-brand hover:text-brand"
                    }`}
                  >
                    {c.name}
                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${activeCategory === c.name ? "bg-white/15 text-white" : "bg-surface text-ink/50"}`}>
                      {c.count}
                    </span>
                  </button>
                ))}
              </div>
              <div className="relative lg:w-64 shrink-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles…"
                  data-testid="blog-search"
                  className="w-full bg-white border border-[#EAEBEA] rounded-full pl-9 pr-4 py-2 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition-colors"
                />
              </div>
            </div>
          </section>

          <section className="bg-white py-14">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              {filtered.length === 0 ? (
                <p data-testid="blog-empty-state" className="py-16 text-center text-ink/50">
                  No articles match your search. Try a different keyword or category.
                </p>
              ) : (
                <>
                  <motion.div key={featured.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Link to={`/blog/${featured.slug}`} data-testid={`article-${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 items-center bg-white border border-[#EAEBEA] rounded-3xl overflow-hidden hover:shadow-[0_24px_50px_-15px_rgba(0,0,0,0.15)] transition-shadow">
                      <div className="h-64 lg:h-[380px] overflow-hidden relative">
                        <img src={featured.image} alt={featured.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                          <Sparkles className="h-3 w-3" /> Featured
                        </span>
                      </div>
                      <div className="p-8 lg:p-12">
                        <div className="flex items-center gap-3 mb-4 text-sm">
                          <span className="bg-brand/10 text-brand font-semibold px-3 py-1 rounded-full">{featured.category}</span>
                          <span className="text-ink/40 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.read_time}</span>
                          <span className="text-ink/40 hidden sm:inline">·</span>
                          <span className="text-ink/40 hidden sm:inline">{fmtDate(featured.date)}</span>
                        </div>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-ink leading-tight group-hover:text-brand transition-colors">{featured.title}</h2>
                        <p className="mt-4 text-ink/60 leading-relaxed">{featured.excerpt}</p>
                        <span className="mt-6 inline-flex items-center gap-2 font-semibold text-ink group-hover:gap-3 transition-all">Read article <ArrowUpRight className="h-4 w-4" /></span>
                      </div>
                    </Link>
                  </motion.div>

                  {rest.length > 0 && (
                    <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
                      {rest.map((a, i) => (
                        <motion.div key={a.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}>
                          <Link to={`/blog/${a.slug}`} data-testid={`article-${a.slug}`} className="group block bg-white border border-[#EAEBEA] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.12)] transition-all duration-300 h-full">
                            <div className="h-52 overflow-hidden">
                              <img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                            </div>
                            <div className="p-6">
                              <div className="flex items-center gap-3 mb-3 text-xs">
                                <span className="bg-surface text-ink/70 font-semibold px-2.5 py-1 rounded-full">{a.category}</span>
                                <span className="text-ink/40">{fmtDate(a.date)}</span>
                              </div>
                              <h3 className="font-heading text-lg font-bold text-ink leading-snug group-hover:text-brand transition-colors">{a.title}</h3>
                              <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-3">{a.excerpt}</p>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}
    </Layout>
  );
}
